import React, { useEffect, useMemo } from 'react';
import { BsX } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, IconButton, Text, Tooltip } from '@chakra-ui/react';

import { getContentHeightString } from '../../../styles/layoutDimensions';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';
import { createMap } from '../../../utils/arrayUtil';

const ChoosePolicyTypesStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const { chooseAnyPolicyType, requireAgentDetails } = useSelector(blueprint.selectOptions);
  const companyPolicyTypes = useSelector(blueprint.selectCompanyPolicyTypes);
  const requirementPolicyTypes = useSelector(blueprint.selectRequirementPolicyTypes);
  const selectedPolicyTypes = useSelector(blueprint.selectSelectedPolicyTypes);
  const requiredPoliciesMap = requirementPolicyTypes?.length ? createMap(requirementPolicyTypes, 'id') : {};

  useEffect(() => {
    if (!chooseAnyPolicyType) {
      dispatch(blueprint.nextStep());
    }
  }, [chooseAnyPolicyType]);

  const addPolicyTypeToList = policyType => {
    if (selectedPolicyTypes.every(pT => pT.id !== policyType.id)) {
      dispatch(blueprint.addSelectedInsurancePolicyType(policyType));
    }
  };

  const policyTypesWithDeletedCustoms = useMemo(() => companyPolicyTypes.map(policyType => {
    if (policyType?.deletedAt && !requiredPoliciesMap[policyType.id]) {
      return null;
    }

    return (
      <Flex
        _hover={{
          cursor: 'pointer',
          boxShadow: 'accent'
        }}
        alignItems="center"
        backgroundColor="backgroundScale.50"
        height="150px"
        justifyContent="center"
        key={policyType.id}
        layerStyle="bgCard"
        m={4}
        p={6}
        textAlign="center"
        transition="0.25s"
        width="200px"
        onClick={() => addPolicyTypeToList(policyType)}
      >
        <Text
          overflow="hidden"
          textOverflow="ellipsis"
          textStyle="bodyXl500"
          whitespace="nowrap"
        >
          {policyType.displayName}
        </Text>
      </Flex>
    );
  }), []);

  return (
    <Flex flexBasis="100%" flexFlow="row nowrap" height={getContentHeightString()} overflow="auto">
      <Flex backgroundColor="white" flexBasis="60%" flexDir="column" p={6}>
        <Text
          mb={4}
          textStyle="displayXs"
        >
          Policy Selection
        </Text>
        <Text>Select the policy types you'd like to submit:</Text>

        <Flex
          alignItems="flex-start"
          flexFlow="row wrap"
          justifyContent="space-around"
          maxH="90%"
          overflow="auto"
        >
          {
            // TODO: add ability to create custom policy type on the fly
            policyTypesWithDeletedCustoms
          }
        </Flex>
      </Flex>
      <Flex backgroundColor="backgroundScale.50" flexBasis="40%" p={6}>
        <Flex
          alignItems="flex-start"
          flexFlow="column nowrap"
          justifyContent="space-between"
          maxH="100%"
          overflow="auto"
          w="100%"
        >
          <Box>
            <Text mb={8} textStyle="displayXs">Selected Policies</Text>

            <Box>
              {selectedPolicyTypes.map(policyType => (
                <Box
                  _hover={{ boxShadow: 'accent' }}
                  key={policyType.id}
                  layerStyle="bgCard"
                  my={4}
                  p={4}
                  transition="0.25s"
                  w="350px"
                >
                  <HStack justifyContent="space-between">
                    <Text
                      overflow="hidden"
                      textOverflow="ellipsis"
                      textStyle="bodyXl500"
                      whitespace="nowrap"
                    >
                      {policyType.displayName}
                    </Text>
                    <IconButton
                      as={BsX}
                      size="sm"
                      variant="textError"
                      onClick={() => dispatch(blueprint.removeSelectedInsurancePolicyType(policyType.id))}
                    />
                  </HStack>
                </Box>
              ))}
            </Box>
          </Box>

          <HStack justifyContent="space-between" mt="auto" w="100%">
            <Button
              variant="secondary"
              onClick={() => dispatch(blueprint.goBack())}
            >
              Back
            </Button>
            <Tooltip
              hasArrow
              isDisabled={!!selectedPolicyTypes.length}
              label="Please select at least one policy type to continue"
            >
              <Box>
                <Button
                  isDisabled={!selectedPolicyTypes.length}
                  variant="gradientSuccess"
                  onClick={() => dispatch(blueprint.nextStep(requireAgentDetails ? 1 : 2))}
                >
                  Start Submission
                </Button>
              </Box>
            </Tooltip>
          </HStack>
        </Flex>
      </Flex>
    </Flex>
  );
};

ChoosePolicyTypesStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default ChoosePolicyTypesStep;
